import React, { useState, useCallback } from "react";
import * as Yup from "yup";
import { Formik, Form as FormikForm, useField } from "formik";
import {
  Form,
  Search,
  Grid,
  Button,
  Label,
  Icon,
  Divider,
  Segment,
  Message,
  type SearchProps,
} from "semantic-ui-react";
import axios from "../../../apis/dvdlocator";
import { debounce } from "lodash";
import "./index.css";
import { parsePartNumber } from "../../../validation/validateSupplyPartNumber";
import type { ProductData } from "./AddSupplyUnitWizard/types";

interface ProductFormProps {
  nextPage: () => void;
  product: ProductData;
  setProduct: (product: ProductData) => void;
  onClose: () => void;
  preselectedSupplyUnits: any[];
}

const FormikField = ({
  label,
  formSubmitted,
  name,
  ...props
}: { label: string; formSubmitted: boolean; name: string; [key: string]: any }) => {
  const [field, meta] = useField(name);
  return (
    <Form.Field>
      <label>{label}</label>
      <Form.Input {...field} {...props} />
      {formSubmitted && meta.error ? (
        <Label basic color="red" pointing>
          {meta.error}
        </Label>
      ) : null}
    </Form.Field>
  );
};

export function ProductForm({
  nextPage,
  product,
  setProduct,
  onClose,
  preselectedSupplyUnits,
}: ProductFormProps) {
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [searchResults, setSearchResults] = useState<{ id: number; title: string }[]>([]);
  const [searchError, setSearchError] = useState("");

  const debouncedSearch = useCallback(
    debounce(async (value: string) => {
      if (!value) {
        setSearchResults([]);
        setSearchError("");
        return;
      }
      setSearchLoading(true);
      setSearchError("");
      try {
        const result = await axios.get(`/families?name=${value}`);
        setSearchResults(result.data.map((e: any) => ({ id: e.id, title: e.family })));
      } catch (err: any) {
        setSearchError(
          err.response?.data?.message ||
            "An error occurred while searching. Please try again."
        );
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 500),
    []
  );

  const handleSearchChange = (_: any, data: SearchProps) => {
    const value = data.value ?? "";
    setSearchValue(value);
    debouncedSearch(value);
  };

  const customResultRenderer = (
    result: { id: number; title: string },
    families: { id: number; title: string }[]
  ) => {
    const isSelected = families.some((f) => f.title === result.title);
    return (
      <div
        style={{
          opacity: isSelected ? 0.5 : 1,
          cursor: isSelected ? "not-allowed" : "pointer",
          padding: "8px",
          color: isSelected ? "#666" : "inherit",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span>{result.title}</span>
          {isSelected && (
            <span style={{ fontSize: "0.8em", color: "#666" }}>(Already Added)</span>
          )}
        </div>
      </div>
    );
  };

  const validationSchema = Yup.object({
    title: Yup.string().min(8).max(250).required("Title is required"),
    partNumber: Yup.string()
      .matches(/^\d+([+-]\d+)*$/, {
        message: "Part Number must contain only numbers separated by '+' or '-'",
        excludeEmptyString: true,
      })
      .test("no-duplicates", "Part Number must not contain duplicate values", (value) => {
        if (!value) return true;
        const allNumbers = parsePartNumber(value);
        return new Set(allNumbers).size === allNumbers.length;
      })
      .test("correct-order", "Part Number values must be in ascending order (e.g 1-2 NOT 2-1)", (value) => {
        if (!value) return true;
        const nums = parsePartNumber(value).map((n) => parseInt(n, 10));
        for (let i = 1; i < nums.length; i++) {
          if (nums[i] < nums[i - 1]) return false;
        }
        return true;
      })
      .test("greater-than-zero", "Part Number values must be greater than 0", (value) => {
        if (!value) return true;
        return parsePartNumber(value).every((n) => Number(n) >= 1);
      }),
  });

  return (
    <Formik
      initialValues={{ ...product }}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={(values, { setSubmitting }) => {
        setProduct({
          title: values.title,
          partNumber: values.partNumber,
          families: values.families,
          product_id: values.product_id,
        });
        setSubmitting(false);
        nextPage();
      }}
    >
      {({ handleSubmit, isSubmitting, submitCount, values, setFieldValue }) => {
        const handleResultSelect = (_: any, { result }: any) => {
          if (values.families.some((f) => f.title === result.title)) return;
          setFieldValue("families", [...values.families, result]);
          setSearchValue("");
        };

        const handleRemoveFamily = (familyId: number) => {
          setFieldValue(
            "families",
            values.families.filter((f) => f.id !== familyId)
          );
        };

        return (
          <Form as={FormikForm} onSubmit={handleSubmit}>
            <Segment basic>
              <FormikField
                label="Title"
                name="title"
                placeholder="Enter Title"
                formSubmitted={submitCount > 0}
              />
              <FormikField
                label="Part Number"
                name="partNumber"
                placeholder="Enter Part Number (optional)"
                formSubmitted={submitCount > 0}
              />
              <Form.Field>
                <label>Families</label>
                <Search
                  fluid
                  input={{ fluid: true }}
                  loading={searchLoading}
                  placeholder="Search Families"
                  onResultSelect={handleResultSelect}
                  onSearchChange={handleSearchChange}
                  results={searchResults}
                  resultRenderer={(result: any) =>
                    customResultRenderer(result, values.families)
                  }
                  minCharacters={2}
                  value={searchValue}
                  className="scrolling-search"
                />
                {searchError && (
                  <Message error content={searchError} style={{ marginTop: "5px" }} />
                )}
                <div style={{ marginTop: "10px" }}>
                  {values.families.map((family) => (
                    <Label key={family.id} style={{ marginRight: "5px", marginBottom: "5px" }}>
                      {family.title}
                      <Icon name="delete" onClick={() => handleRemoveFamily(family.id)} />
                    </Label>
                  ))}
                </div>
              </Form.Field>
            </Segment>

            <Divider />
            <Segment basic>
              <Grid>
                <Grid.Row>
                  <Grid.Column width={8}>
                    <Button type="button" onClick={onClose} floated="left">
                      Cancel
                    </Button>
                  </Grid.Column>
                  <Grid.Column width={8} textAlign="right">
                    <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                      {preselectedSupplyUnits.length > 0 && (
                        <Label color="grey" style={{ marginRight: "10px" }} size="large">
                          {`${preselectedSupplyUnits.length} supply unit(s) preselected`}
                        </Label>
                      )}
                      <Button type="submit" primary disabled={isSubmitting}>
                        Next
                      </Button>
                    </div>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Segment>
          </Form>
        );
      }}
    </Formik>
  );
}
